import {
  MutationCache,
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { OverlayToken } from '@efira/contracts';
import type { DirectorCommandName, OverlayPresencePayload } from '@efira/protocol';

import { ApiError, request, upload } from './client';
import { reportApiError } from './errors';

/**
 * Загрузка данных dashboard.
 *
 * Решение владельца 2026-09-11 (docs/DECISIONS.md, блок 4б): TanStack Query
 * подключается в dashboard и НЕ подключается в overlay - там состояние
 * приходит по сокету, а не запросами, и лишний вес в бюджете overlay не
 * оправдан по смыслу.
 *
 * Порог окупаемости, который наступил: кэш между экранами (overlay-токен
 * нужен и зоне Overlay, и зоне Эфира) и инвалидация после мутаций (ротация
 * токена и смена сцены меняют то, что уже показано на другом экране). Пока
 * запрос был один, ручной fetch в эффекте был дешевле библиотеки; на третьем
 * месте он стал дороже.
 */

export const queryClient = new QueryClient({
  /**
   * НИ ОДНА МУТАЦИЯ НЕ ЗАВЕРШАЕТСЯ ТИХО.
   *
   * Обработчик стоит на кэше мутаций, а не у каждой по отдельности: правило,
   * требующее внимательности на каждом вызове, однажды будет забыто - и
   * забытым окажется именно тот вызов, который сломается. Так в блоке 5в три
   * операции молча отваливались с кодом 400, и это был четвёртый случай
   * молчаливого отказа в проекте.
   */
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // `handled: true` - экран показывает отказ сам, и общий тост стал бы
      // вторым сообщением об одном и том же. Отказ помечается ЯВНО и виден в
      // коде: молчание по умолчанию - это то, что мы чиним.
      if (mutation.meta?.handled !== true) {
        reportApiError(error);
      }
    },
  }),
  defaultOptions: {
    queries: {
      // Dashboard управляет эфиром: данные, которым больше полминуты, лучше
      // перепросить, чем показать устаревшими.
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

export const QUERY_KEYS = {
  overlayToken: ['overlay-token'] as const,
  overlayPresence: ['overlay-presence'] as const,
  scene: ['scene'] as const,
  channels: ['channels'] as const,
  preflight: ['preflight'] as const,
  me: ['me'] as const,
  quota: ['quota'] as const,
  gifts: ['gifts'] as const,
  bindings: (gameId: string) => ['bindings', gameId] as const,
  gameInfo: ['game-info'] as const,
  overlayStyle: (gameId: string) => ['overlay-style', gameId] as const,
  widgets: ['widgets'] as const,
  sounds: ['sounds'] as const,
  soundLibrary: (query: string) => ['sound-library', query] as const,
  gifs: (query: string, page: number) => ['gifs', query, page] as const,
  music: ['music'] as const,
  stream: ['stream'] as const,
  account: ['account'] as const,
  analytics: (query: string) => ['analytics', query] as const,
  plan: ['plan'] as const,
  billing: ['billing'] as const,
  affiliate: ['affiliate'] as const,
  history: ['history'] as const,
};

/** Запросы идут ТОЛЬКО через общий клиент - см. `client.ts`. */
async function getJson<T>(path: string): Promise<T> {
  return request<T>(path);
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body });
}

export function useOverlayToken() {
  return useQuery({
    queryKey: QUERY_KEYS.overlayToken,
    queryFn: () => getJson<OverlayToken>('/api/overlay-token'),
  });
}

export function useRotateOverlayToken() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => request<OverlayToken>('/api/overlay-token/rotate', { method: 'POST' }),
    onSuccess: (data) => {
      // Ротация меняет то, что уже показано: кладём результат в кэш сразу, без
      // повторного запроса. Ровно этого не хватало ручным fetch-ам.
      client.setQueryData(QUERY_KEYS.overlayToken, data);
    },
  });
}

/**
 * Присутствие overlay.
 *
 * Первое значение - запросом, дальше обновления приходят сокетом и кладутся в
 * тот же кэш через `setOverlayPresence`. Поэтому автоматическое обновление по
 * интервалу здесь выключено: опрос дублировал бы поток событий.
 */
export function useOverlayPresence() {
  return useQuery({
    queryKey: QUERY_KEYS.overlayPresence,
    queryFn: () => getJson<OverlayPresencePayload>('/api/overlay-presence'),
    refetchInterval: false,
  });
}

export function setOverlayPresence(client: QueryClient, presence: OverlayPresencePayload): void {
  client.setQueryData(QUERY_KEYS.overlayPresence, presence);
}

export interface ScenePayload {
  sceneId: string | null;
}

export function useSetScene() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (sceneId: string | null) => postJson<ScenePayload>('/api/scene', { sceneId }),
    onSuccess: (data) => {
      client.setQueryData(QUERY_KEYS.scene, data);
      // Экран привязок читает активную сцену из аккаунта (`me`): без
      // инвалидации он ещё полминуты показывал бы «сначала выберите сцену».
      void client.invalidateQueries({ queryKey: QUERY_KEYS.me });
    },
  });
}

// ── TikTok-каналы ───────────────────────────────────────────────────────────

export interface Channel {
  id: string;
  handle: string;
  displayName: string | null;
  lastLiveAt: string | null;
  tiktokUserId: string | null;
}

export interface ChannelsPayload {
  channels: Channel[];
  activeChannelId: string | null;
  /** null - без ограничения (Pro). */
  limit: number | null;
}

export function useChannels() {
  return useQuery({
    queryKey: QUERY_KEYS.channels,
    queryFn: () => getJson<ChannelsPayload>('/api/channels'),
  });
}

export interface AttachError {
  error: 'channel_limit_reached' | 'bound_to_other_account' | 'rebind_window_active';
  limit?: number;
  availableAt?: string;
}

export function useAttachChannel() {
  const client = useQueryClient();

  return useMutation({
    // Отказ разбирается ЭКРАНОМ: у «лимит исчерпан» и «подождите до даты»
    // разные действия, и различать их должен интерфейс, а не текст тоста.
    // Пометка `handled` - явный отказ от общего тоста, видимый в коде.
    meta: { handled: true },
    mutationFn: async (handle: string) => {
      try {
        return await request<{ channel: Channel }>('/api/channels', {
          method: 'POST',
          body: { handle },
        });
      } catch (error) {
        throw error instanceof ApiError
          ? ({ error: error.code ?? 'unknown', ...(error.body as object) } as AttachError)
          : error;
      }
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: QUERY_KEYS.channels });
    },
  });
}

export function useDetachChannel() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (channelId: string) =>
      request<{ ok: true }>(`/api/channels/${channelId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: QUERY_KEYS.channels });
    },
  });
}

export function useActivateChannel() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (channelId: string) => postJson(`/api/channels/${channelId}/activate`, {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: QUERY_KEYS.channels });
    },
  });
}

// ── Эфир ────────────────────────────────────────────────────────────────────

export interface LiveStatus {
  status: 'idle' | 'connecting' | 'live' | 'failed';
  stage?: string;
  message?: string;
  reason?: string;
  action?: string;
  sourceId?: 'tiktok' | 'synthetic';
  handle?: string;
  since?: string;
}

export interface Preflight {
  /**
   * Игра, которую тариф не пустит в эфир, названная ДО нажатия.
   * null - ограничения нет.
   */
  planBlocksGame: { id: string; title: string } | null;
  overlayOpen: boolean;
  channelSelected: boolean;
  channelHandle: string | null;
  sources: { tiktok: boolean; synthetic: boolean };
  connection: LiveStatus;
  session: { minutesSoFar: number; realEvents: number; syntheticEvents: number } | null;
}

export function usePreflight() {
  return useQuery({
    queryKey: QUERY_KEYS.preflight,
    queryFn: () => getJson<Preflight>('/api/live/preflight'),
    // Чеклист проверяется перед подключением, и данные в нём быстро устаревают:
    // overlay могли открыть только что.
    staleTime: 5_000,
  });
}

export function useConnectLive() {
  const client = useQueryClient();

  return useMutation({
    // Отказ подключения показывает сам экран эфира: у каждой причины свой
    // текст и своё действие, и они уже написаны по-человечески.
    meta: { handled: true },
    mutationFn: async (input: { mode: 'tiktok' | 'synthetic'; intensity?: string }) => {
      try {
        return await request<LiveStatus>('/api/live/connect', { method: 'POST', body: input });
      } catch (error) {
        throw error instanceof ApiError ? (error.body as LiveStatus) : error;
      }
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: QUERY_KEYS.preflight });
    },
  });
}

export function useDisconnectLive() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => postJson('/api/live/disconnect', {}),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: QUERY_KEYS.preflight });
    },
  });
}

// ── Привязки действий ───────────────────────────────────────────────────────

export interface GiftRow {
  id: string;
  vendorId: string | null;
  name: string;
  coins: number;
  icon: string | null;
  streakable: boolean;
  /** Показывается ли зрителю в панели. null - вендор признака не прислал. */
  onPanel: boolean | null;
  /** Подарок можно привязать. false - у него нет идентификатора вендора. */
  selectable: boolean;
  /** Почему нельзя. Показывается вместо молчаливой неактивности. */
  unselectableReason: string | null;
  priceLocal: { amount: number; currency: string } | null;
}

export interface GiftsPayload {
  gifts: GiftRow[];
  currency: string;
  currencyGuessed: boolean;
  currencies: string[];
  total: number;
  withVendorId: number;
  updatedAt: string | null;
}

export function useGifts() {
  return useQuery({
    queryKey: QUERY_KEYS.gifts,
    queryFn: () => getJson<GiftsPayload>('/api/gifts'),
    // Справочник меняется выгрузкой, а не по ходу работы: перепрашивать его
    // каждые полминуты незачем.
    staleTime: 10 * 60_000,
  });
}

export type TriggerCondition =
  | { event: 'gift'; giftId: string }
  | { event: 'gift'; anyGift: true; minCoins: number }
  | { event: 'comment'; keywordKey: string }
  | { event: 'like'; likeThreshold: number }
  | { event: 'follow' }
  | { event: 'share' }
  | { event: 'join' };

export type ExtraRequirement =
  { kind: 'follower' } | { kind: 'subscriber' } | { kind: 'minCoins'; coins: number };

export interface Binding {
  key: string;
  actionId: string;
  params?: Record<string, string | number>;
  conditions: TriggerCondition[];
  requires?: ExtraRequirement[];
  multiplier?: number;
  cooldownMs?: number;
  priority?: number;
  enabled?: boolean;
}

export interface ActionParam {
  id: string;
  type: 'enum' | 'number' | 'text' | 'collection' | 'gift';
  labelKey: string;
  scope: 'action' | 'binding';
  values?: string[];
  collectionId?: string;
  min?: number;
  max?: number;
  default?: string | number;
}

export interface BindableAction {
  id: string;
  labelKey: string;
  effectKey: string;
  params: ActionParam[];
  sign?: 'positive' | 'negative';
  /** Предел числа условий. Показывается числом, а не исчезновением кнопки. */
  maxConditions?: number;
}

export interface Collection {
  id: string;
  labelKey: string;
  source:
    | { kind: 'enum'; values: string[] }
    | { kind: 'directory'; directoryId: string }
    | { kind: 'slots'; count: number };
  min: number;
  max: number;
  default: string[];
  applyMoment: 'immediate' | 'immediate-with-restart' | 'next-round' | 'restart';
}

export interface SettingField {
  id: string;
  type: 'boolean' | 'number' | 'text' | 'select' | 'editor-link';
  labelKey: string;
  /** Ключ подписи группы настроек. Без него поле идёт в общий список. */
  groupKey?: string;
  default?: unknown;
  values?: string[];
  min?: number;
  max?: number;
  visibleWhen?: { field: string; equals: unknown };
  editor?: {
    editorId: string;
    dataScope: 'game' | 'account' | 'library';
    quota?: { limit: number; labelKey: string };
  };
}

export interface BindingPreset {
  id: string;
  labelKey: string;
  bindings: Binding[];
}

export interface LegendEntry {
  bindingKey: string;
  actionId: string;
  labelKey: string;
  trigger:
    | { kind: 'gift'; giftId: string; coins: number }
    | { kind: 'anyGift'; minCoins: number }
    | { kind: 'keyword'; word: string }
    | { kind: 'likes'; threshold: number }
    | { kind: 'plain'; event: string };
  priceLocal?: { amount: number; currency: string };
  multiplier?: number;
  cooldownMs?: number;
}

export interface BindingsPayload {
  gameId: string;
  bindings: Binding[];
  manifest: {
    actions: BindableAction[];
    collections: Collection[];
    settings: { fields: SettingField[] };
    chatAccess: { direct: boolean; reasonKey?: string };
    presets: BindingPreset[];
  };
  legend: { entries: LegendEntry[] };
  /** Действующие значения настроек: заводские, уточнённые правками стримера. */
  settingValues: Record<string, boolean | number | string>;
  /** Какие из них изменены. Две карты, чтобы «изменено» стояло там, где изменено. */
  settingsChanged: string[];
  settingsFactory: Record<string, boolean | number | string>;
  /** Словарь игры: подписи приходят с сервера, а не зашиты в клиент. */
  dictionary: Record<string, string>;
  currency: string;
  currencyGuessed: boolean;
}

/**
 * Каталог игр - единственный источник списка (ARCHITECTURE 2.2).
 *
 * Ни одно число об играх не пишется руками во второй раз: и сетка выбора, и
 * счётчики, и лендинг читают один и тот же список. У конкурента источника нет,
 * и расхождение числа игр доехало до сниппета в поисковой выдаче.
 */
export interface CatalogCategory {
  id: string;
  title: Record<string, string>;
  description: Record<string, string>;
  /** Сколько готовых игр в категории. Считает сервер по реестру. */
  count: number;
}

export interface CatalogGame {
  id: string;
  title: Record<string, string>;
  tagline: Record<string, string>;
  category: string;
  /** Готовые адреса обложки в нашем CDN по вариантам размера. */
  cover: Record<'tile' | 'tile@2x' | 'card' | 'card@2x', string>;
  worldClass: string;
  renderMode: string;
  free: boolean;
  wave: number;
  order: number;
  status: 'ready' | 'planned';
  /** Подключена ли игра кодом прямо сейчас. */
  playable: boolean;
  /** Команды ведущего, которые игра принимает: пауза, снятие, перезапуск. */
  directorCommands: DirectorCommandName[];
  /** Значок каталога: правило с числами живёт на сервере (`world/badges.ts`). */
  badge: 'new' | 'popular' | null;
  /** Ролик предпросмотра для карточки при наведении или `null`. */
  preview: string | null;
  /** Чем играют зрители: слова стримера, собранные из заводских привязок. */
  plays: string[];
}

/**
 * Редактор раскладки overlay.
 *
 * Состав элементов приезжает от overlay при монтировании сцены: знать их может
 * только тот код, который их рисует. Смещения хранятся отдельно и только для
 * тех элементов, которые стример реально трогал.
 */
export interface HudElementSpec {
  id: string;
  labelKey?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  stretch?: 'x' | 'y' | null;
  /** Части, лежащие поверх этой по построению (см. protocol/layout.ts). */
  over?: string[];
}

export interface ElementPlacement {
  x?: number;
  y?: number;
  scale?: number;
  stretch?: number;
  hidden?: boolean;
  /** Слой: что поверх чего. Порядок слоёв правит стример. */
  z?: number;
}

export type OverlayLayout = Record<string, ElementPlacement>;

export interface LayoutPayload {
  gameId: string;
  elements: HudElementSpec[];
  layout: OverlayLayout;
  /** Элементы, заходящие в охранные зоны. Предупреждение, а не запрет. */
  warnings: string[];
  dictionary: Record<string, string>;
  /** Игры, у которых есть настроенная раскладка - для переноса. */
  sources: { gameId: string; elements: number }[];
}

export function useLayout(gameId: string | null) {
  return useQuery({
    queryKey: ['layout', gameId ?? ''],
    queryFn: () => getJson<LayoutPayload>(`/api/games/${gameId ?? ''}/layout`),
    enabled: gameId !== null,
    /*
     * ПОКА СОСТАВА НЕТ - СПРАШИВАТЬ СНОВА.
     *
     * Состав частей сообщает оверлей, когда рисует сцену (`reportLayout`), а
     * отдельного сигнала о нём у dashboard нет. Режим, открытый раньше отчёта,
     * - свежий аккаунт, первая игра, медленный browser source, - получал пустой
     * состав и больше его не спрашивал: экран говорил «подождите», но ожидание
     * ничего не меняло до выхода из режима. Найдено на раннере CI (u23b,
     * `console-layout-default`): там оверлей рисует первую сцену позже нажатия.
     * Опрос идёт ТОЛЬКО пока состав пуст и прекращается с первым ответом с
     * частями.
     */
    refetchInterval: (query) => (query.state.data?.elements.length === 0 ? 1000 : false),
  });
}

export function useSaveLayout(gameId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (layout: OverlayLayout) =>
      sendJson<{ layout: OverlayLayout; warnings: string[] }>(
        `/api/games/${gameId}/layout`,
        'PUT',
        { layout },
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['layout', gameId] }),
  });
}

export function useResetLayout(gameId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () =>
      sendJson<{ layout: OverlayLayout }>(`/api/games/${gameId}/layout/reset`, 'POST'),
    onSuccess: () => client.invalidateQueries({ queryKey: ['layout', gameId] }),
  });
}

/** Перенос раскладки из другой игры - по совпадающим идентификаторам. */
export function useCopyLayout(gameId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (from: string) =>
      sendJson<{ copied: string[]; skipped: string[] }>(
        `/api/games/${gameId}/layout/copy`,
        'POST',
        {
          from,
        },
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['layout', gameId] }),
  });
}

export function useCatalog() {
  return useQuery({
    queryKey: ['catalog'],
    queryFn: () => getJson<{ categories: CatalogCategory[]; games: CatalogGame[] }>('/api/catalog'),
    staleTime: 10 * 60 * 1000,
  });
}

export function useBindings(gameId: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.bindings(gameId ?? ''),
    queryFn: () => getJson<BindingsPayload>(`/api/games/${gameId ?? ''}/bindings`),
    enabled: gameId !== null,
  });
}

async function sendJson<T>(path: string, method: 'POST' | 'PUT' | 'DELETE', body?: unknown) {
  return request<T>(path, { method, ...(body === undefined ? {} : { body }) });
}

/**
 * Правка привязки.
 *
 * Применяется НА ЛЕТУ: сервер перечитывает привязки живого эфира без
 * переподключения к источнику. Переподключение стоило бы вызова квоты
 * вендора, а суточных вызовов сто - стример, правящий привязки по ходу эфира,
 * выбрал бы их за час.
 */
/**
 * Правка настройки игры.
 *
 * Применяется на лету: сервер перечитывает настройки мира и переиздаёт
 * контракт. Перезапуск игры ради настройки был бы худшим из вариантов - у
 * конкурента смена источника числа посреди раунда подвешивает игру и требует
 * принудительного перезапуска с потерей раунда.
 */
export function useSaveSetting(gameId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: { id: string; value: boolean | number | string }) =>
      sendJson<{ ok: true }>(
        `/api/games/${gameId}/settings/${encodeURIComponent(input.id)}`,
        'PUT',
        { value: input.value },
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.bindings(gameId) }),
  });
}

/** Возврат ОДНОГО поля к заводскому - отдельно от сброса всей игры. */
export function useRestoreSetting(gameId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      sendJson<{ ok: true }>(
        `/api/games/${gameId}/settings/${encodeURIComponent(id)}/restore`,
        'POST',
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.bindings(gameId) }),
  });
}

export function useSaveBinding(gameId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (binding: Binding) =>
      sendJson<{ ok: true }>(
        `/api/games/${gameId}/bindings/${encodeURIComponent(binding.key)}`,
        'PUT',
        binding,
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.bindings(gameId) }),
  });
}

/**
 * Добавить привязку к действию.
 *
 * Ключ выдаёт СЕРВЕР: приставка, по которой хранилище отличает добавленную
 * привязку от заводской, - его правило, и клиент о ней знать не должен.
 */
export function useAddBinding(gameId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: { actionId: string; params?: Record<string, string | number> }) =>
      sendJson<{ binding: Binding }>(`/api/games/${gameId}/bindings`, 'POST', input),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.bindings(gameId) }),
  });
}

export function useDeleteBinding(gameId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (key: string) =>
      sendJson<{ ok: true }>(`/api/games/${gameId}/bindings/${encodeURIComponent(key)}`, 'DELETE'),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.bindings(gameId) }),
  });
}

/** Возврат элемента к ЗАВОДСКОМУ значению, а не к пустому. */
export function useRestoreBinding(gameId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (key: string) =>
      sendJson<{ binding: Binding }>(
        `/api/games/${gameId}/bindings/${encodeURIComponent(key)}/restore`,
        'POST',
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.bindings(gameId) }),
  });
}

export function useResetGameBindings(gameId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => sendJson<{ ok: true }>(`/api/games/${gameId}/bindings/reset`, 'POST'),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.bindings(gameId) }),
  });
}

export function useApplyPreset(gameId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (presetId: string) =>
      sendJson<{ bindings: Binding[] }>(
        `/api/games/${gameId}/presets/${encodeURIComponent(presetId)}`,
        'POST',
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.bindings(gameId) }),
  });
}

/** Прогон одной привязки: то же событие, поданное в тот же вычислитель. */
export function useRunBinding() {
  return useMutation({
    mutationFn: (event: unknown) =>
      sendJson<{ fired: number }>('/api/live/run-binding', 'POST', { event }),
  });
}

/**
 * Показатели хода игры - секция «Сведения об игре» панели игры.
 *
 * ОПРАШИВАЕТСЯ, А НЕ ПОДПИСЫВАЕТСЯ. Канал состояния идёт десять раз в секунду
 * и предназначен кадру: подписав на него ещё и панель, мы получили бы третье
 * соединение и перерисовку колонки на каждом тике. Здесь нужна не анимация, а
 * число раз в две секунды.
 */
export interface GameInfoPayload {
  running: boolean;
  gameId?: string;
  round?: number;
  paused?: boolean;
  rows: { id: string; label: string; value: string }[];
}

export function useGameInfo(enabled: boolean) {
  return useQuery({
    queryKey: QUERY_KEYS.gameInfo,
    queryFn: () => getJson<GameInfoPayload>('/api/live/game-info'),
    enabled,
    refetchInterval: 2000,
  });
}

/**
 * Тестовое действие: выполнить действие игры в кадре.
 *
 * Не то же, что прогон привязки: там подаётся СОБЫТИЕ и проверяется, сработает
 * ли правило; здесь подаётся САМО ДЕЙСТВИЕ и проверяется, как оно выглядит.
 */
export function useRunAction() {
  return useMutation({
    mutationFn: (input: { actionId: string; params?: Record<string, string | number> }) =>
      sendJson<{ fired: true }>('/api/live/run-action', 'POST', input),
  });
}

/** Оформление кадра: показ легенды и фон. Настройка платформы, не игры. */
export interface OverlayStylePayload {
  values: Record<string, boolean | number | string>;
  changed: string[];
  factory: Record<string, boolean | number | string>;
  limits: Record<string, { min: number; max: number; step: number }>;
}

export function useOverlayStyle(gameId: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.overlayStyle(gameId ?? ''),
    queryFn: () => getJson<OverlayStylePayload>(`/api/games/${gameId ?? ''}/overlay-style`),
    enabled: gameId !== null,
  });
}

export function useSaveOverlayStyle(gameId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: { id: string; value: boolean | number | string }) =>
      sendJson<{ values: Record<string, boolean | number | string>; changed: string[] }>(
        `/api/games/${gameId}/overlay-style/${encodeURIComponent(input.id)}`,
        'PUT',
        { value: input.value },
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.overlayStyle(gameId) }),
  });
}

export function useResetOverlayStyle(gameId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => sendJson<{ ok: true }>(`/api/games/${gameId}/overlay-style/reset`, 'POST'),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.overlayStyle(gameId) }),
  });
}

/** Сброс ВСЕХ настроек игры к заводским - отдельно от поэлементного возврата. */
export function useResetGameSettings(gameId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => sendJson<{ ok: true }>(`/api/games/${gameId}/settings/reset`, 'POST'),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.bindings(gameId) }),
  });
}

/**
 * Виджеты оверлея.
 *
 * Живут на аккаунте; где стоит экземпляр - в раскладке по играм; показывать
 * ли его в этой игре - свойство игры. Три разных вопроса, три разных запроса.
 */
export interface WidgetInstanceRow {
  id: string;
  type: string;
  label: string;
  style: string;
  accent: string;
  showValue: boolean;
  showIcon: boolean;
  card: boolean;
  target?: number;
  rows?: number;
  mediaUrl?: string;
  /** Какие виды есть у этого типа. Приходит из каталога сервера. */
  styles: readonly string[];
}

export interface WidgetsPayload {
  enabled: boolean;
  instances: WidgetInstanceRow[];
  /** Скрытые в текущей игре. */
  hidden: string[];
  gameId: string | null;
  catalog: { type: string; label: string; styles: readonly string[]; pro: boolean }[];
}

export function useWidgets() {
  return useQuery({
    queryKey: QUERY_KEYS.widgets,
    queryFn: async () => {
      const payload = await getJson<WidgetsPayload>('/api/widgets');

      /* Виды приклеиваются к экземпляру здесь: сервер отдаёт их каталогом
         типов, и таскать их копию в каждом экземпляре по сети незачем. */
      return {
        ...payload,
        instances: payload.instances.map((instance) => ({
          ...instance,
          styles: payload.catalog.find((card) => card.type === instance.type)?.styles ?? [],
        })),
      };
    },
  });
}

function widgetMutation<TInput>(send: (input: TInput) => Promise<unknown>): {
  mutationFn: (input: TInput) => Promise<unknown>;
} {
  return { mutationFn: send };
}

export function useAddWidget() {
  const client = useQueryClient();

  return useMutation({
    ...widgetMutation((type: string) => sendJson<WidgetsPayload>('/api/widgets', 'POST', { type })),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.widgets }),
  });
}

export function usePatchWidget() {
  const client = useQueryClient();

  return useMutation({
    ...widgetMutation((input: { id: string } & Partial<WidgetInstanceRow>) => {
      const { id, ...patch } = input;
      return sendJson<WidgetsPayload>(`/api/widgets/${encodeURIComponent(id)}`, 'PUT', patch);
    }),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.widgets }),
  });
}

export function useRemoveWidget() {
  const client = useQueryClient();

  return useMutation({
    ...widgetMutation((id: string) =>
      sendJson<WidgetsPayload>(`/api/widgets/${encodeURIComponent(id)}`, 'DELETE'),
    ),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.widgets }),
  });
}

export function useSetWidgetsEnabled() {
  const client = useQueryClient();

  return useMutation({
    ...widgetMutation((enabled: boolean) =>
      sendJson<WidgetsPayload>('/api/widgets/enabled', 'PUT', { enabled }),
    ),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.widgets }),
  });
}

export function useSetWidgetShown(gameId: string) {
  const client = useQueryClient();

  return useMutation({
    ...widgetMutation((input: { id: string; shown: boolean }) =>
      sendJson<{ hidden: string[] }>(
        `/api/games/${gameId}/widgets/${encodeURIComponent(input.id)}`,
        'PUT',
        { shown: input.shown },
      ),
    ),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.widgets }),
  });
}

export function useSetCurrency() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (currency: string) =>
      sendJson<{ currency: string }>('/api/account/currency', 'POST', { currency }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: QUERY_KEYS.gifts });
      void client.invalidateQueries({ queryKey: ['bindings'] });
    },
  });
}

export interface MePayload {
  user: {
    id: string;
    email: string;
    name: string;
    emailVerified: boolean;
    timezone: string;
    currency: string | null;
    activeSceneId: string | null;
    plan: {
      id: string;
      name: string;
      dailyMinutes: number | null;
      maxChannels: number | null;
      watermark: boolean;
      /** Открыты ли платные игры каталога. */
      paidGames: boolean;
    };
  } | null;
  currency: string;
  currencyGuessed: boolean;
  currencies: string[];
}

export interface AuthConfigPayload {
  google: boolean;
  discord: boolean;
}

/** Какие способы входа настроены на этом сервере. Публично, без сессии. */
/**
 * Команда ведущего: пауза, снятие паузы, перезапуск раунда.
 *
 * Отказ показывает сам экран - у каждой причины свой текст и своё действие,
 * и молчаливого отказа быть не должно: стример жмёт кнопку в эфире.
 */
export function useDirectorCommand() {
  return useMutation({
    meta: { handled: true },
    mutationFn: async (name: DirectorCommandName) => {
      try {
        return await postJson<{ applied: boolean; paused: boolean }>('/api/live/director/command', {
          name,
        });
      } catch (error) {
        throw error instanceof ApiError ? (error.body as { reason?: string }) : error;
      }
    },
  });
}

/** Сообщение ведущего зрителям в кадр: текст и длительность показа. */
export function useDirectorMessage() {
  return useMutation({
    meta: { handled: true },
    mutationFn: async (input: { text: string; seconds: number }) => {
      try {
        return await postJson<{ sent: boolean; seconds: number }>(
          '/api/live/director/message',
          input,
        );
      } catch (error) {
        throw error instanceof ApiError ? (error.body as { reason?: string }) : error;
      }
    },
  });
}

export function useAuthConfig() {
  return useQuery({
    queryKey: ['auth-config'] as const,
    queryFn: () => getJson<AuthConfigPayload>('/api/auth-config'),
    staleTime: Infinity,
  });
}

export function useMe() {
  return useQuery({
    queryKey: QUERY_KEYS.me,
    queryFn: () => getJson<MePayload>('/api/me'),
  });
}

// ── Дневная квота ───────────────────────────────────────────────────────────

export interface QuotaPayload {
  dailyMinutes: number | null;
  usedMinutes: number;
  remainingMinutes: number | null;
  resetsAt: string | null;
  exhausted: boolean;
  warning: 10 | 2 | null;
  localDate: string;
  timezone: string;
}

/**
 * Остаток квоты.
 *
 * Первое значение - запросом, дальше обновления приезжают каналом
 * `session:config` и кладутся в тот же кэш через `setQuota`. Опрос по
 * интервалу выключен: он дублировал бы рассылку, а расходились бы они молча.
 */
export function useQuota() {
  return useQuery({
    queryKey: QUERY_KEYS.quota,
    queryFn: () => getJson<QuotaPayload>('/api/quota'),
    refetchInterval: false,
  });
}

export function setQuota(client: QueryClient, quota: QuotaPayload): void {
  client.setQueryData(QUERY_KEYS.quota, quota);
}

/* ─── Звуковой контур ──────────────────────────────────────────────────── */

export interface SoundRow {
  id: string;
  name: string;
  file: string;
  url: string;
  origin: 'library' | 'upload' | 'link';
  bytes: number;
  color: string;
  enabled: boolean;
  trigger: { type: string; keyword?: string; giftId?: string; minCoins?: number };
}

export interface SoundsPayload {
  sounds: SoundRow[];
  pads: number;
  mixer: Record<string, { volume: number; muted: boolean }>;
  limits: {
    padsMin: number;
    padsMax: number;
    padsDefault: number;
    perAccount: number;
    maxBytes: number;
    volumeStep: number;
  };
  /** Сколько позиций в НАШЕМ каталоге. Настоящее число, а не «1 665». */
  library: number;
}

export interface SoundLibraryPayload {
  total: number;
  categories: { id: string; count: number }[];
  items: {
    id: string;
    name: { ru: string; en: string };
    category: string;
    ms: number;
    url: string;
  }[];
}

export function useSounds() {
  return useQuery({
    queryKey: QUERY_KEYS.sounds,
    queryFn: () => getJson<SoundsPayload>('/api/sounds'),
  });
}

export function useSoundLibrary(query: string) {
  return useQuery({
    queryKey: QUERY_KEYS.soundLibrary(query),
    queryFn: () =>
      getJson<SoundLibraryPayload>(`/api/sound-library?q=${encodeURIComponent(query)}`),
  });
}

/** Общая обёртка: после любой правки список звуков перечитывается. */
function soundMutation<TInput>(send: (input: TInput) => Promise<unknown>) {
  return { mutationFn: send };
}

export function useAddLibrarySound() {
  const client = useQueryClient();

  return useMutation({
    ...soundMutation((itemId: string) => sendJson('/api/sounds/library', 'POST', { itemId })),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.sounds }),
  });
}

export function useUploadSound() {
  const client = useQueryClient();

  return useMutation({
    ...soundMutation((file: File) =>
      upload(
        `/api/sounds/upload?name=${encodeURIComponent(file.name.replace(/\.[^.]+$/, ''))}`,
        file,
      ),
    ),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.sounds }),
  });
}

export function useAddSoundByUrl() {
  const client = useQueryClient();

  return useMutation({
    ...soundMutation((input: { url: string; name: string }) =>
      sendJson('/api/sounds/link', 'POST', input),
    ),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.sounds }),
  });
}

export function usePatchSound() {
  const client = useQueryClient();

  return useMutation({
    ...soundMutation((input: { id: string } & Partial<SoundRow>) => {
      const { id, ...patch } = input;
      return sendJson(`/api/sounds/${encodeURIComponent(id)}`, 'PUT', patch);
    }),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.sounds }),
  });
}

export function useRemoveSound() {
  const client = useQueryClient();

  return useMutation({
    ...soundMutation((id: string) => sendJson(`/api/sounds/${encodeURIComponent(id)}`, 'DELETE')),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.sounds }),
  });
}

export function useReorderSounds() {
  const client = useQueryClient();

  return useMutation({
    ...soundMutation((ids: string[]) => sendJson('/api/sounds/order', 'PUT', { ids })),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.sounds }),
  });
}

export function useSetPads() {
  const client = useQueryClient();

  return useMutation({
    ...soundMutation((count: number) => sendJson('/api/sounds/pads', 'PUT', { count })),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.sounds }),
  });
}

export function useSetMixer() {
  const client = useQueryClient();

  return useMutation({
    mutationKey: ['sounds', 'mixer'],
    /*
     * ЗАПРОСЫ МИКШЕРА ИДУТ ПО ОЧЕРЕДИ, А НЕ НАПЕРЕГОНКИ.
     *
     * Каждый шаг громкости - свой PUT. Отправленные одновременно, они
     * приходят на сервер в любом порядке, и последним записанным
     * оказывается не последнее движение человека: на экране 50 %, в базе
     * 60 %. Общая область (`scope`) заставляет их выполняться друг за
     * другом - порядок сохраняется, побеждает последнее движение.
     */
    scope: { id: 'sounds-mixer' },
    ...soundMutation((input: { channel: string; volume?: number; muted?: boolean }) =>
      sendJson('/api/sounds/mixer', 'PUT', input),
    ),
    /*
     * ПОСЛЕДНЕЕ СЛОВО - ЗА ПОСЛЕДНИМ ДВИЖЕНИЕМ, А НЕ ЗА ПОСЛЕДНИМ ОТВЕТОМ.
     *
     * Громкость меняют подряд: стрелкой с клавиатуры или тянут ручку.
     * Каждый шаг - свой запрос, и каждый ответ обновлял список звуков.
     * Ответ на ШАГ РАНЬШЕ приходил позже своего шага, перетирал значение
     * в кэше устаревшим, и следующий шаг считался уже от него: на экране
     * 50 %, на сервере 60 %. Поймано сценарием микшера, который читает
     * состояние с сервера после перезагрузки.
     *
     * Пока в полёте есть ещё хоть один запрос микшера, список не
     * обновляется: его обновит последний.
     */
    onSuccess: () => {
      if (client.isMutating({ mutationKey: ['sounds', 'mixer'] }) > 1) return;

      void client.invalidateQueries({ queryKey: QUERY_KEYS.sounds });
    },
  });
}

/**
 * Нажатие пада: команда ОВЕРЛЕЮ.
 *
 * Не `new Audio()` в консоли: браузер стримера в OBS не захватывается, и звук
 * услышал бы только он сам. Прослушивание в библиотеке настроек - другое
 * дело, там оно локальное и так и подписано.
 */
export function usePlaySound() {
  return useMutation({
    mutationFn: (id: string) => sendJson(`/api/sounds/${encodeURIComponent(id)}/play`, 'POST'),
  });
}

export function useStopSounds() {
  return useMutation({ mutationFn: () => sendJson('/api/sounds/stop', 'POST') });
}

/* ─── Гифки ────────────────────────────────────────────────────────────── */

export interface GifItemRow {
  id: string;
  url: string;
  preview: string;
  title: string;
  sponsored?: boolean;
}

export function useGifs(query: string, page: number, enabled: boolean) {
  return useQuery({
    queryKey: QUERY_KEYS.gifs(query, page),
    enabled,
    queryFn: () =>
      getJson<{ items: GifItemRow[]; more: boolean }>(
        `/api/gifs?q=${encodeURIComponent(query)}&page=${page}`,
      ),
    /* Страница уже кэширована на сервере; здесь кэш только чтобы не дёргать
       наш маршрут на каждый рендер. */
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useShowGif() {
  return useMutation({
    mutationFn: (input: { url: string; seconds?: number }) =>
      sendJson('/api/gifs/show', 'POST', input),
  });
}

/* ─── Музыка ───────────────────────────────────────────────────────────── */

export interface TrackRow {
  id: string;
  name: string;
  url: string;
  bytes: number;
}

export interface MusicPayload {
  tracks: TrackRow[];
  limit: number;
  maxBytes: number;
  rightsSeen: boolean;
}

export function useMusic() {
  return useQuery({
    queryKey: QUERY_KEYS.music,
    queryFn: () => getJson<MusicPayload>('/api/music'),
  });
}

export function useUploadTrack() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (file: File) =>
      upload(
        `/api/music/upload?name=${encodeURIComponent(file.name.replace(/\.[^.]+$/, ''))}`,
        file,
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.music }),
  });
}

export function useMusicMutation<TInput>(send: (input: TInput) => Promise<unknown>) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: send,
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.music }),
  });
}

export function useRenameTrack() {
  return useMusicMutation((input: { id: string; name: string }) =>
    sendJson(`/api/music/${encodeURIComponent(input.id)}`, 'PUT', { name: input.name }),
  );
}

export function useRemoveTrack() {
  return useMusicMutation((id: string) =>
    sendJson(`/api/music/${encodeURIComponent(id)}`, 'DELETE'),
  );
}

export function useClearMusic() {
  return useMusicMutation(() => sendJson('/api/music', 'DELETE'));
}

export function useReorderMusic() {
  return useMusicMutation((ids: string[]) => sendJson('/api/music/order', 'PUT', { ids }));
}

export function useMusicRightsSeen() {
  return useMusicMutation(() => sendJson('/api/music/rights', 'PUT'));
}

export function useMusicCommand() {
  return useMutation({
    mutationFn: (input: { action: string; trackId?: string; position?: number }) =>
      sendJson('/api/music/command', 'POST', input),
  });
}

/* ─── Настройки эфира и эффекты подарков ───────────────────────────────── */

export interface StreamPayload {
  reader: boolean;
  alerts: boolean;
  giftFx: boolean;
  voice: string | null;
  readShoutouts: boolean;
  seconds: number;
  profanity: boolean;
  customWords: string[];
  shoutout: { user: string; plain: string };
  giftCards: Record<
    string,
    { giftId: string; effect: string; enabled: boolean; muted: boolean; seconds: number }
  >;
  voices: { name: string; lang: string }[];
  effects: string[];
  tokens: string[];
}

export function useStream() {
  return useQuery({
    queryKey: QUERY_KEYS.stream,
    queryFn: () => getJson<StreamPayload>('/api/stream'),
  });
}

export function useSaveStream() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (patch: Partial<StreamPayload>) => sendJson('/api/stream', 'PUT', patch),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.stream }),
  });
}

export function useSaveGiftCard() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (card: {
      giftId: string;
      effect?: string;
      enabled?: boolean;
      muted?: boolean;
      seconds?: number;
    }) => sendJson('/api/stream/gift-card', 'PUT', card),
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.stream }),
  });
}

export function useSendGiftCard() {
  return useMutation({
    mutationFn: (input: { giftId: string; count?: number }) =>
      sendJson('/api/stream/gift-card/send', 'POST', input),
  });
}

/* ─── Аккаунт ──────────────────────────────────────────────────────────── */

export interface AccountPayload {
  name: string;
  email: string;
  emailVerified: boolean;
  featured: boolean;
  deleteAt: string | null;
  providers: { id: string; linked: boolean }[];
  hasPassword: boolean;
  deleteWindowDays: number;
}

export function useAccount() {
  return useQuery({
    queryKey: QUERY_KEYS.account,
    queryFn: () => getJson<AccountPayload>('/api/account'),
  });
}

function useAccountMutation<TInput>(send: (input: TInput) => Promise<unknown>) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: send,
    onSuccess: () => client.invalidateQueries({ queryKey: QUERY_KEYS.account }),
  });
}

export function useSaveAccount() {
  return useAccountMutation((patch: { name?: string; featured?: boolean }) =>
    sendJson('/api/account', 'PUT', patch),
  );
}

export function useUnlinkProvider() {
  return useAccountMutation((id: string) =>
    sendJson(`/api/account/provider/${encodeURIComponent(id)}`, 'DELETE'),
  );
}

export function useDeleteAccount() {
  return useAccountMutation((confirm: string) =>
    sendJson('/api/account/delete', 'POST', { confirm }),
  );
}

export function useCancelDelete() {
  return useAccountMutation(() => sendJson('/api/account/delete/cancel', 'POST'));
}

/* ─── Аналитика и история ──────────────────────────────────────────────── */

export interface AnalyticsTotals {
  runs: number;
  minutes: number;
  comments: number;
  gifts: number;
  giftCoins: number;
  likes: number;
  shares: number;
  follows: number;
  viewers: number;
}

export interface AnalyticsPayload {
  range: string;
  from: string | null;
  to: string | null;
  totals: AnalyticsTotals;
  games: { gameId: string; runs: number; minutes: number }[];
  days: { date: string; runs: number; minutes: number }[];
  filters: {
    games: { id: string; title: string }[];
    channels: { id: string; handle: string }[];
  };
}

/**
 * Аналитика за период с фильтрами.
 *
 * Ключ запроса - строка адреса: период и фильтры живут в адресе окна, и
 * кэш обязан различать их так же, как их различает человек по ссылке.
 */
export function useAnalytics(search: string) {
  return useQuery({
    queryKey: QUERY_KEYS.analytics(search),
    queryFn: () => getJson<AnalyticsPayload>(`/api/analytics${search === '' ? '' : `?${search}`}`),
  });
}

export interface HistoryRun {
  id: string;
  startedAt: string;
  endedAt: string | null;
  minutes: number;
  endReason: string | null;
  channel: string | null;
  gameIds: string[];
  comments: number;
  gifts: number;
  giftCoins: number;
  viewers: number;
}

export function useHistory() {
  return useQuery({
    queryKey: QUERY_KEYS.history,
    queryFn: () => getJson<{ runs: HistoryRun[] }>('/api/history'),
  });
}

/* ─── Тариф ────────────────────────────────────────────────────────────── */

export interface PlanLine {
  title: string;
  free: string;
  pro: string;
  /** Значение одинаково на обоих тарифах: это предел платформы, а не тарифа. */
  same?: boolean;
  /** Откуда взято число - для ворот, не для человека. */
  source: string;
}

export interface PlanPayload {
  current: { id: string; name: string };
  timezone: string;
  daily: {
    limit: number | null;
    used: number;
    remaining: number | null;
    resetAt: string | null;
  } | null;
  lines: PlanLine[];
  /** Цена Pro. Единственный источник - сервер (`world/pricing.ts`). */
  price: {
    currency: string;
    sign: string;
    periods: {
      period: 'month' | 'year';
      minor: number;
      text: string;
      perMonthText: string;
    }[];
    saving: { percent: number; savedMinor: number; text: string };
  };
  checkout: { provider: string; connected: boolean };
  interestedAt: string | null;
}

export type BillingPeriod = 'month' | 'year';

export interface BillingPayload {
  plan: { id: string; name: string };
  provider: { id: string; connected: boolean };
  subscription: {
    status: 'active' | 'cancelling' | 'past_due' | 'ended';
    period: BillingPeriod;
    currentPeriodEnd: string | null;
    cancelledAt: string | null;
    graceEndsAt: string | null;
    atProvider: boolean;
  } | null;
  nextCharge: { at: string; text: string; approximate: boolean } | null;
  retrying: boolean;
  method: { known: boolean; text: string };
  payments: {
    id: string;
    paidAt: string;
    text: string;
    period: BillingPeriod;
    receiptUrl: string | null;
  }[];
  emptyHint: string;
}

export interface AffiliatePayload {
  code: string | null;
  link: string | null;
  reward: 'money' | 'months';
  rewardChangeableAt: string | null;
  stats: {
    clicks: number;
    signups: number;
    paying: number;
    accruedMinor: number;
    availableMinor: number;
    heldMinor: number;
    reversedMinor: number;
    monthsEarned: number;
  };
  terms: {
    sharePercent: number;
    holdDays: number;
    clickWindowDays: number;
    thresholdMinor: number;
    thresholdText: string;
    payoutPeriod: string;
    version: string;
    monthPriceMinor: number;
  };
  accruals: {
    id: string;
    at: string;
    amountMinor: number;
    text: string;
    status: 'held' | 'available' | 'granted' | 'reversed';
    heldUntil: string | null;
  }[];
}

/** Кабинет партнёра. Устройство едет с запросом: по нему отсекается самопривод. */
export function useAffiliate(device: string) {
  return useQuery({
    queryKey: QUERY_KEYS.affiliate,
    queryFn: () => getJson<AffiliatePayload>(`/api/affiliate?device=${encodeURIComponent(device)}`),
  });
}

/** Создать партнёрскую ссылку. Повтор возвращает ту же. */
export function useCreateAffiliateLink() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (device: string) => postJson<AffiliatePayload>('/api/affiliate/link', { device }),
    onSuccess: (data) => {
      client.setQueryData<AffiliatePayload>(QUERY_KEYS.affiliate, data);
    },
  });
}

/** Дорожка вознаграждения: деньги или месяцы Pro. */
export function useAffiliateReward() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (reward: 'money' | 'months') =>
      postJson<{ ok: boolean; message?: string }>('/api/affiliate/reward', { reward }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: QUERY_KEYS.affiliate });
    },
  });
}

export function useBilling() {
  return useQuery({
    queryKey: QUERY_KEYS.billing,
    queryFn: () => getJson<BillingPayload>('/api/billing'),
  });
}

/**
 * Оформление оплаты.
 *
 * Отказ «оплата не подключена» - НЕ ошибка запроса: это сегодняшнее
 * состояние продукта, и приходит оно двухсоткой с причиной. Тост об ошибке
 * здесь сказал бы неправду.
 */
export function useCheckout() {
  return useMutation({
    mutationFn: (period: BillingPeriod) =>
      postJson<{ ok: boolean; url?: string; reason?: string; message?: string }>(
        '/api/billing/checkout',
        { period },
      ),
  });
}

/**
 * Отмена подписки: до конца оплаченного периода.
 *
 * Ответ сервера НЕ ВЫБРАСЫВАЕТСЯ: он говорит, приняли ли отмену у
 * провайдера. Первая редакция показывала «подписка отменена» всегда - и в
 * том случае, когда у платёжного сервиса она осталась и списания
 * продолжатся. Это была самая дорогая строка контура (разбор текстов).
 */
export function useCancelSubscription() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () =>
      postJson<{ status: string; provider: string; message: string | null }>(
        '/api/billing/cancel',
        {},
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: QUERY_KEYS.billing });
      void client.invalidateQueries({ queryKey: QUERY_KEYS.plan });
    },
  });
}

/** Возврат отменённой подписки. Ответ провайдера тоже доезжает до человека. */
export function useResumeSubscription() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () =>
      postJson<{ status: string; provider: string; message: string | null }>(
        '/api/billing/resume',
        {},
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: QUERY_KEYS.billing });
      void client.invalidateQueries({ queryKey: QUERY_KEYS.plan });
    },
  });
}

/** Что будет при смене периода - до подтверждения. */
export function usePeriodChange(period: BillingPeriod, enabled: boolean) {
  return useQuery({
    queryKey: [...QUERY_KEYS.billing, 'period', period] as const,
    enabled,
    queryFn: () =>
      getJson<{
        from: BillingPeriod;
        to: BillingPeriod;
        lines: string[];
        periodEnd: string | null;
        possible: boolean;
      }>(`/api/billing/period-change?period=${period}`),
  });
}

/** Смена периода. Без провайдера отвечает отказом с причиной, а не молчанием. */
export function useChangePeriod() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (period: BillingPeriod) =>
      postJson<{ ok: boolean; reason?: string; message?: string }>('/api/billing/period', {
        period,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: QUERY_KEYS.billing });
    },
  });
}

export function usePlan() {
  return useQuery({
    queryKey: QUERY_KEYS.plan,
    queryFn: () => getJson<PlanPayload>('/api/plan'),
  });
}

/**
 * Отметить желание получить Pro.
 *
 * Единственное действие окна тарифа: чекаута нет, списать нечего. Ответ
 * кладётся в кэш сразу - человек должен увидеть, что его отметили.
 */
export function useProInterest() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => postJson<{ interestedAt: string }>('/api/plan/interest', {}),
    onSuccess: (data) => {
      client.setQueryData<PlanPayload>(QUERY_KEYS.plan, (current) =>
        current === undefined ? current : { ...current, interestedAt: data.interestedAt },
      );
    },
  });
}
